import React, { useState, useEffect, useCallback } from "react";
import { WhatsAppConversation, WhatsAppMessage } from "../../types/api";
import { MessageList } from "./MessageList";
import { MessageInput } from "./MessageInput";
import { WhatsAppTemplatePicker } from "./WhatsAppTemplatePicker";
import { useWhatsAppMessages } from "../../hooks/useWhatsAppMessages";
import { useWhatsAppSSE } from "../../hooks/useWhatsAppSSE";
import apiService from "../../services/api";
import { normalizePhone } from "../../utils/phoneUtils";

const STATUS_OPTIONS: { value: 'open' | 'solved' | 'pending' | 'campaign'; label: string; color: string }[] = [
  { value: 'open',     label: 'Open',     color: '#25D366' },
  { value: 'pending',  label: 'Pending',  color: '#f59e0b' },
  { value: 'solved',   label: 'Solved',   color: '#9ca3af' },
  { value: 'campaign', label: 'Campaign', color: '#8b5cf6' },
];

const EXPIRED_COLOR = '#ef4444';

interface WhatsAppConversationPopupProps {
  phoneNumber: string;
  patientName?: string;
  patientHmId?: string;
  patientId?: number;
  currentUserId: number;
  canSendFreeform?: boolean;
  onClose: () => void;
  onUnreadChange?: (count: number) => void;
}

export const WhatsAppConversationPopup: React.FC<WhatsAppConversationPopupProps> = ({
  phoneNumber,
  patientName,
  patientHmId,
  patientId,
  currentUserId,
  canSendFreeform = true,
  onClose,
  onUnreadChange,
}) => {
  const token = localStorage.getItem("token") || "";
  const phone = normalizePhone(phoneNumber);

  const [isSending, setIsSending] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [templateDraft, setTemplateDraft] = useState<{ body: string; templateId: string; variables?: Record<string, string> } | null>(null);
  // undefined = not yet loaded, null = loaded but no existing conversation
  const [conversation, setConversation] = useState<WhatsAppConversation | null | undefined>(undefined);

  const { messages, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage, refetch, addMessage, addOptimisticMessage, updateMessage } =
    useWhatsAppMessages(phone, token);

  // Load conversation metadata (status, assigned user)
  // Reload messages from DB every time the popup opens
  useEffect(() => { refetch(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    apiService.getWhatsAppConversations({ token, search: phone, limit: 5 })
      .then((res) => {
        const conv = res.conversations.find((c) =>
          c.phone_number.replace(/^0(\d{9})$/, "972$1") === phone
        ) || res.conversations[0] || null;
        setConversation(conv);
      })
      .catch(() => {});
  }, [phone]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mark read on open
  useEffect(() => {
    if (!phone) return;
    apiService.markWhatsAppRead({ token, phone_number: phone }).catch(() => {});
    onUnreadChange?.(0);
  }, [phone]); // eslint-disable-line react-hooks/exhaustive-deps

  // SSE for real-time messages
  useWhatsAppSSE(phone, true, {
    onStatusUpdate: (msg: WhatsAppMessage) => {
      updateMessage(msg);
    },
    onNewMessage: (msg: WhatsAppMessage) => {
      addMessage(msg);
      if (msg.direction === "inbound") {
        // Patient replied — re-enable freeform input immediately
        setConversation((prev) => prev
          ? { ...prev, can_send_freeform: true }
          : { phone_number: phone, can_send_freeform: true, unread_count: 0 } as any
        );
        apiService.markWhatsAppRead({ token, phone_number: phone }).catch(() => {});
        onUnreadChange?.(0);
      }
    },
    onConversationStatusChanged: (status: string) => {
      setConversation((prev) => prev ? { ...prev, status: status as any } : prev);
    },
  });

  const handleSend = useCallback(
    async (text: string, mediaUrl?: string, templateId?: string, variables?: Record<string, string>) => {
      setIsSending(true);
      // Show optimistic message immediately; the echo webhook will create the real record
      // and the SSE new_message event will replace this placeholder.
      if (text) addOptimisticMessage(text, phone, !!templateId);
      try {
        await apiService.sendWhatsAppMessage({
          token,
          phone_number: phone,
          patient_id: patientId,
          conversation_id: conversation?.id,
          body: text,
          media_url: mediaUrl,
          template_id: templateId,
          template_variables: variables,
        });
      } catch (e) {
        console.error("[WhatsAppPopup] send failed", e);
      } finally {
        setIsSending(false);
      }
    },
    [token, phone, patientId, conversation?.id, addOptimisticMessage]
  );

  useEffect(() => {
    if (templateDraft) {
      handleSend(templateDraft.body, undefined, templateDraft.templateId, templateDraft.variables);
      setTemplateDraft(null);
    }
  }, [templateDraft, handleSend]);

  const handleStatusChange = async (status: 'open' | 'solved' | 'pending') => {
    try {
      await apiService.updateWhatsAppConversationStatus({ token, phone_number: phone, status });
      // Use the requested status directly — don't rely on response format
      setConversation((prev) => prev ? { ...prev, status } : prev);
    } catch (e: any) {
      const msg = e?.response?.data?.detail || "לא ניתן לשנות סטטוס";
      alert(msg);
    }
  };


  const currentStatus = (conversation && conversation.status) || 'open';
  const statusColor = STATUS_OPTIONS.find((s) => s.value === currentStatus)?.color || '#25D366';
  // freeform rules:
  // - undefined (still loading): show nothing yet — use prop default
  // - null (no conversation): no prior inbound message → template only
  // - conversation exists: use backend's can_send_freeform flag
  const effectiveCanSendFreeform =
    conversation === undefined ? canSendFreeform :
    conversation === null ? false :
    (conversation.can_send_freeform ?? false);

  // Expired = conversation loaded, exists, but outside 24h freeform window
  const isExpired = conversation !== undefined && conversation !== null && !effectiveCanSendFreeform;

  const controlStyle: React.CSSProperties = {
    background: "rgba(255,255,255,0.15)",
    border: "1px solid rgba(255,255,255,0.35)",
    borderRadius: 6,
    color: "#fff",
    cursor: "pointer",
    fontSize: 11,
    padding: "3px 7px",
    outline: "none",
    height: 26,
  };

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 1000 }} />

      {/* Popup panel */}
      <div
        style={{
          position: "fixed",
          bottom: 24,
          left: 24,
          width: 600,
          height: 620,
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
          zIndex: 1001,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — RTL layout: X | controls | spacer | name+phone | icon */}
        <div
          style={{
            backgroundColor: "#3A7380",
            color: "#fff",
            padding: "0 12px",
            height: 56,
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexShrink: 0,
          }}
        >
          {/* Status dot + label/dropdown — hidden while conversation is still loading */}
          {conversation !== undefined && (
            <div style={{ display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: isExpired ? EXPIRED_COLOR : statusColor, display: "inline-block" }} />
              {isExpired ? (
                <span style={{ ...controlStyle, cursor: "default", opacity: 0.9 }}>Expired</span>
              ) : (
                <select
                  value={currentStatus}
                  onChange={(e) => handleStatusChange(e.target.value as any)}
                  disabled={currentStatus === 'campaign'}
                  style={{ ...controlStyle, ...(currentStatus === 'campaign' ? { opacity: 0.7, cursor: "default" } : {}) }}
                >
                  {STATUS_OPTIONS.filter((s) =>
                    !(currentStatus === 'solved' && s.value === 'pending') &&
                    !(currentStatus !== 'campaign' && s.value === 'campaign')
                  ).map((s) => (
                    <option key={s.value} value={s.value} style={{ color: "#000" }}>{s.label}</option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Spacer */}
          <div style={{ flex: 1 }} />

          {/* Patient name + phone (right-aligned, RTL) */}
          <div style={{ textAlign: "right", minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 14, direction: "rtl", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {patientName || phoneNumber}
            </div>
            <div style={{ fontSize: 11, opacity: 0.8, direction: "ltr", whiteSpace: "nowrap" }}>
              {phone}{patientHmId ? ` · ${patientHmId}` : ""}
            </div>
          </div>

          {/* Close */}
          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.15)",
              border: "none",
              borderRadius: 6,
              color: "#fff",
              cursor: "pointer",
              fontSize: 14,
              width: 28,
              height: 28,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            ✕
          </button>
        </div>

        {/* Messages */}
        {isLoading ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#e5ddd5", color: "#888", fontSize: 13 }}>
            טוען הודעות...
          </div>
        ) : (
          <MessageList
            messages={messages}
            currentUserId={currentUserId}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={fetchNextPage}
          />
        )}

        {/* Input */}
        <MessageInput
          onSend={(text, mediaUrl) => handleSend(text, mediaUrl)}
          onTemplateClick={() => setShowTemplatePicker(true)}
          disabled={isSending}
          canSendFreeform={effectiveCanSendFreeform}
        />
      </div>

      {showTemplatePicker && (
        <div style={{ position: "fixed", zIndex: 1002 }}>
          <WhatsAppTemplatePicker
            onSelect={(body, templateId, variables) => {
              setTemplateDraft({ body, templateId, variables });
              setShowTemplatePicker(false);
            }}
            onClose={() => setShowTemplatePicker(false)}
            patient={patientName ? {
              full_name: patientName,
              first_name: patientName.split(" ")[0],
              last_name: patientName.split(" ").slice(1).join(" "),
              hm_id: patientHmId,
              phone,
            } : undefined}
          />
        </div>
      )}
    </>
  );
};
